import { Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { BenchmarkingData } from '@blisspointmedia/bpm-types/dist/Benchmarking';
import { lessThanIgnoreTime } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { toPrettyPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  computeCustomRelativeRange,
  DateRange
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { getBenchmarkingChannels, ScpConfig } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { ValueLabelRecord } from '@blisspointmedia/bpm-types/dist/App2Performance';
import { Header } from '@Components/StickyTable/BPMTable';
import { Brand50, Neutral400, Neutral500 } from '../../utils/colors';

export interface TransformedDatum {
  date: string;
  [key: string]: number | string | null;
}

export type TransformedData = TransformedDatum[];

// we have so few clients running these channels our sample sizes are too small if we spit into tiers
export const NO_SPEND_TERCILE_CHANNELS = new Set(['audio', 'display']);

export const ENABLED_BENCHMARKING_CHANNELS: ScpConfig[] = getBenchmarkingChannels();

export const MORE_INFO_COPY = [
  'Benchmarks are grouped into Spend Terciles which divide all accounts into three equal-sized groups (Low, Medium, and High) based on their average daily spend over the last two years (<33%, 33–66%, >66% of the total spender population).',
  'Verticals (industry categories) are pre-assigned at a company level to ensure relevant peer comparison.',
  'The values displayed for each tercile represent the median result, which is the middle value for that group.'
];

export const TERCILE_PRETTY_NAME_MAP = {
  tercile_1: 'High Spenders',
  tercile_2: 'Medium Spenders',
  tercile_3: 'Low Spenders'
};

export const TERCILE_DATA_PRETTY_NAME_MAP = {
  median: 'Median',
  lower: 'Lower CI',
  upper: 'Upper CI'
};

export const COLOR_MAP = {
  tercile_1: Neutral500,
  tercile_2: '#A9B0BB',
  tercile_3: Neutral400,
  company: Brand50
};

export const CAPITALIZED_DATE_PERIODS_MAP = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly'
};

export const DATE_PERIODS_TO_NOUN = {
  daily: 'Day',
  weekly: 'Week',
  monthly: 'Month'
};

export const ALL = 'All';
export const ALL_VERTICALS_OPTION = { value: ALL, label: 'All Vertical' };
export const DISABLED_AUDIO_VERTICALS = new Set([
  'Finance, B2B & High-Value Services',
  'Media, Tech, Hobbies & Other'
]);
export const VERTICAL_DISABLED_TEXT = 'No spend groups have a large enough sample size';
export const VERTICAL_DISABLED_PROPS: VerticalOptionExtra = {
  disabled: true,
  simpleTooltip: VERTICAL_DISABLED_TEXT,
  simpleTooltipVerticalShift: '-55px'
};

export const getInitDateRange = (): DateRange => {
  const range = computeCustomRelativeRange(
    {
      number: 92,
      interval: 'Days',
      before: true,
      referenceDate: 'Yesterday'
    },
    {
      number: 3,
      interval: 'Days',
      before: true,
      referenceDate: 'Yesterday'
    },
    {}
  );
  if (!range) {
    throw new Error('computeCustomRelativeRange returned undefined for benchmarking init range');
  }
  return range;
};

export const transformData = (
  data: BenchmarkingData | Record<string, unknown>
): TransformedData => {
  const data2 = {};
  for (const tercile of Object.keys(data)) {
    for (const date of Object.keys(data[tercile])) {
      if (!data2[date]) {
        data2[date] = {};
      }
      for (const metric of Object.keys(data[tercile][date])) {
        data2[date][`${tercile}_${metric}`] = data[tercile][date][metric];
      }
    }
  }
  const finalData: TransformedData = [];
  for (const date of Object.keys(data2)) {
    finalData.push({ date, ...data2[date] });
  }
  return finalData.sort((a, b) => (lessThanIgnoreTime(a.date, b.date) ? -1 : 1));
};

export const formatPercent = (val: number): string => toPrettyPercent(val, { zeroLabel: '0%' });

export const renderWithPercentChangeOption =
  (
    func: NonNullable<Header['renderer']>,
    isPercentChange: boolean
  ): NonNullable<Header['renderer']> =>
  (item, index) => {
    if (index === 0 && isPercentChange) {
      return 'N/A';
    }
    return func(item, index);
  };

export const sortPayloadByCompanyThenValue = (
  a: Payload<ValueType, string | number>,
  b: Payload<ValueType, string | number>
): number => {
  if ((a.dataKey as string).includes('company')) {
    return -1;
  }
  if ((b.dataKey as string).includes('company')) {
    return 1;
  }
  return (b.value as number) - (a.value as number);
};

interface VerticalOptionExtra {
  disabled?: boolean;
  simpleTooltip?: string;
  simpleTooltipVerticalShift?: string;
}
export type VerticalOption = ValueLabelRecord & VerticalOptionExtra;
export type VerticalOptions = VerticalOption[];

export const makeKpiColNameFromAlias = (kpi: string, colName: string): string =>
  `${kpi}_${colName}`;
